<script lang="ts" setup>

import { reactive, computed } from "vue";
import * as XLSX from "xlsx";

import * as relatoriosService from "@/services/relatoriosService";
import { fdata, SimNao } from "@/utils/funcoes";
import { useOrdenacao } from "@/composables/useOrdenacao";

import BreadCrumb from "@/components/Common/BreadCrumb.vue";
import Notificacao from "@/components/Notificacao/Index.vue";
import TrataTecla from "@/components/TrataTecla/Index.vue";
import DropdownCidadesAtiva from "@/components/Dropdowns/DropdownCidadesAtiva.vue";
import DropdownEmpresas from "@/components/Dropdowns/DropdownEmpresas.vue";

import ReportView from "@/components/ReportView/Index.vue";
import ReportCabecalho from "@/components/ReportView/Cabecalho.vue";

import RelNormal from "@/pages/Relatorios/Clientes/relNormal.vue";
import RelFuneral from "@/pages/Relatorios/Clientes/relFuneral.vue";
import RelSeguro from "@/pages/Relatorios/Clientes/relSeguro.vue";

const apresentacoes = ["Normal", "Assistência Funeral", "Seguro"];

const colunasOpcionais = [
  { chave: "cpf", label: "CPF" },
  { chave: "nascimento", label: "Nasc." },
  { chave: "idade", label: "Idade" },
  { chave: "telefone", label: "Telefone" },
  { chave: "cidade", label: "Cidade" },
  { chave: "ativo", label: "Ativo" },
];

const controle = reactive({
  filtros: {
    ativo: true,
    cidadeId: null,
    empresaId: null,
    comDependentes: true,
    dataNascimentoInicial: null,
    dataNascimentoFinal: null,
  } as relatoriosService.FiltroRelatorioClientes,

  apresentacao: "Normal",

  usarIdadeInicial: true,
  usarIdadeFinal: true,
  idadeInicial: null as number | null,
  idadeFinal: null as number | null,

  colunasVisiveis: {
    cpf: true,
    nascimento: true,
    idade: true,
    telefone: true,
    cidade: true,
    ativo: true,
  } as Record<string, boolean>,

  cidadeNome: "" as string,
  empresaNome: "" as string,

  itens: [] as relatoriosService.RelClienteDTO[],
  totalRegistros: 0,

  erro: "",
  status: "",
  pesquisando: false,
});

function calcIdade(dataNascimento: string | null): number | null {
  if (!dataNascimento) return null;
  const nascimento = new Date(dataNascimento);
  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const aindaNaoFezAniversario =
    hoje.getMonth() < nascimento.getMonth() ||
    (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() < nascimento.getDate());
  if (aindaNaoFezAniversario) idade--;
  return idade;
}

function idadeParaData(idade: number | null): string | null {
  if (idade === null || idade === undefined || isNaN(idade)) return null;
  const hoje = new Date();
  const data = new Date(hoje.getFullYear() - idade, hoje.getMonth(), hoje.getDate());
  return data.toISOString().substring(0, 10);
}

function dataParaIdade(data: string | null): number | null {
  if (!data) return null;
  return calcIdade(data);
}

// Idade e data de nascimento andam em ordem invertida: quem é mais velho tem data de nascimento MENOR.
// "Idade Inicial" (idade mínima) equivale ao limite MAIS RECENTE de nascimento (Dt Nasc Final).
// "Idade Final" (idade máxima) equivale ao limite MAIS ANTIGO de nascimento (Dt Nasc Inicial).
function alternarIdadeInicial() {
  controle.usarIdadeInicial = !controle.usarIdadeInicial;
  if (controle.usarIdadeInicial) {
    controle.idadeInicial = dataParaIdade(controle.filtros.dataNascimentoFinal);
  } else {
    controle.filtros.dataNascimentoFinal = idadeParaData(controle.idadeInicial);
  }
}

function alternarIdadeFinal() {
  controle.usarIdadeFinal = !controle.usarIdadeFinal;
  if (controle.usarIdadeFinal) {
    controle.idadeFinal = dataParaIdade(controle.filtros.dataNascimentoInicial);
  } else {
    controle.filtros.dataNascimentoInicial = idadeParaData(controle.idadeFinal);
  }
}

function atualizarDataPelaIdadeInicial() {
  controle.filtros.dataNascimentoFinal = idadeParaData(controle.idadeInicial);
}

function atualizarDataPelaIdadeFinal() {
  controle.filtros.dataNascimentoInicial = idadeParaData(controle.idadeFinal);
}

function selecionouCidade(cidade: any) {
  controle.cidadeNome = cidade ? `${cidade.nome} - ${cidade.estado}` : "";
}

function selecionouEmpresa(empresa: any) {
  controle.empresaNome = empresa ? empresa.nome : "";
}

const resumoFiltros = computed(() => {
  const partes = [
    `Apresentação: ${controle.apresentacao}`,
    `Status: ${controle.filtros.ativo === true ? "Ativos" : controle.filtros.ativo === false ? "Inativos" : "Todos"}`,
    `Cidade: ${controle.filtros.cidadeId ? controle.cidadeNome : "Todas"}`,
    `Empresa: ${controle.filtros.empresaId ? controle.empresaNome : "Todas"}`,
    `Com Dependentes: ${controle.filtros.comDependentes === true ? "Sim" : controle.filtros.comDependentes === false ? "Não" : "Todos"}`,
    `Dt Nascimento: ${controle.filtros.dataNascimentoInicial ? fdata(controle.filtros.dataNascimentoInicial) : "..."} a ${controle.filtros.dataNascimentoFinal ? fdata(controle.filtros.dataNascimentoFinal) : "..."}`,
  ];
  return partes.join("  |  ");
});

interface LinhaPessoa {
  nome: string;
  dataNascimento: string | null;
  cpf: string | null;
  tipo: string;
}

function dataDentroDoFiltro(data: string | null): boolean {
  if (!controle.filtros.dataNascimentoInicial && !controle.filtros.dataNascimentoFinal) return true;
  if (!data) return false;

  const d = data.substring(0, 10);

  if (controle.filtros.dataNascimentoInicial && d < controle.filtros.dataNascimentoInicial) return false;
  if (controle.filtros.dataNascimentoFinal && d > controle.filtros.dataNascimentoFinal) return false;

  return true;
}

const sortNormal = useOrdenacao("nome");
const sortPessoas = useOrdenacao("nome");

const itensOrdenados = computed(() => sortNormal.aplicar(controle.itens, {
  idade: (item: relatoriosService.RelClienteDTO) => calcIdade(item.dataNascimento) ?? -1,
  nascimento: (item: relatoriosService.RelClienteDTO) => item.dataNascimento || "",
  telefone: (item: relatoriosService.RelClienteDTO) => item.celular || item.telefone || "",
  cidade: (item: relatoriosService.RelClienteDTO) => item.cidadeNome || "",
}));

const linhasPessoas = computed<LinhaPessoa[]>(() => {
  const linhas: LinhaPessoa[] = [];

  // Nos modos Assistência Funeral / Seguro o filtro de data é aplicado por pessoa (titular e cada dependente),
  // não só pelo titular como no modo Normal.
  const aplicarFiltroData = controle.apresentacao !== "Normal";

  controle.itens.forEach((cliente) => {
    if (!aplicarFiltroData || dataDentroDoFiltro(cliente.dataNascimento)) {
      linhas.push({ nome: cliente.nome, dataNascimento: cliente.dataNascimento, cpf: cliente.cpf, tipo: "Titular" });
    }

    if (controle.apresentacao === "Assistência Funeral") {
      cliente.dependentes
        .filter((dep) => !aplicarFiltroData || dataDentroDoFiltro(dep.dataNascimento))
        .forEach((dep) => {
          linhas.push({ nome: dep.nome, dataNascimento: dep.dataNascimento, cpf: dep.cpf, tipo: dep.parentesco || "Dependente" });
        });
    } else if (controle.apresentacao === "Seguro") {
      cliente.dependentes
        .filter((dep) => (dep.parentesco || "").toLowerCase().includes("conjuge") || (dep.parentesco || "").toLowerCase().includes("cônjuge"))
        .filter((dep) => !aplicarFiltroData || dataDentroDoFiltro(dep.dataNascimento))
        .forEach((dep) => {
          linhas.push({ nome: dep.nome, dataNascimento: dep.dataNascimento, cpf: dep.cpf, tipo: dep.parentesco || "Cônjuge" });
        });
    }
  });

  return linhas;
});

const linhasPessoasOrdenadas = computed(() => sortPessoas.aplicar(linhasPessoas.value, {
  dataNascimento: (item: LinhaPessoa) => item.dataNascimento || "",
}));

async function pesquisar() {
  controle.pesquisando = true;
  controle.erro = "";
  try {
    // Nos modos Assistência Funeral / Seguro o filtro de data não é enviado ao backend (que filtraria só pelo
    // titular); a filtragem por pessoa é feita depois, em linhasPessoas.
    const filtrosEnvio = { ...controle.filtros };
    if (controle.apresentacao !== "Normal") {
      filtrosEnvio.dataNascimentoInicial = null;
      filtrosEnvio.dataNascimentoFinal = null;
    }

    const r = await relatoriosService.clientes(filtrosEnvio);
    controle.totalRegistros = r.totalRegistros;
    controle.itens = r.dados;
  } catch (e: any) {
    controle.erro = e?.message || "Erro ao pesquisar clientes";
  } finally {
    controle.pesquisando = false;
  }
}

function gerarExcel() {
  const pagina = document.getElementById("pagina");
  if (!pagina) return;

  const tabelas = pagina.querySelectorAll("table");
  if (tabelas.length === 0) return;

  // Mesmas colunas exibidas na tela (e no CSV): lê direto do DOM da tabela renderizada,
  // então funciona igual nos 3 modos de apresentação (Normal/Assistência Funeral/Seguro).
  // Todo valor é mantido como texto (string) — inclusive o CPF — pra não deixar o Excel
  // reinterpretar como número/data e perder zero à esquerda.
  const linhas: string[][] = [];
  tabelas.forEach((t) => {
    t.querySelectorAll("tr").forEach((tr) => {
      const celulas = [...tr.querySelectorAll("th,td")].map((c) => (c.textContent || "").trim());
      linhas.push(celulas);
    });
  });

  const planilha = XLSX.utils.aoa_to_sheet(linhas);
  const livro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(livro, planilha, "Clientes");
  XLSX.writeFile(livro, "Rel Clientes.xlsx");
}

</script>

<template>
  <BreadCrumb PageTitle="Relatório de Clientes" :items="[
    { label: 'Relatórios', to: '/relatorios' },
    { label: 'Clientes', to: '/relatorios/clientes' }]" />

  <Notificacao :erro="controle.erro" @limparErro="controle.erro = ''" :progresso="controle.status"
    @limparProgresso="controle.status = ''" />

  <TrataTecla>
    <div class="card mb-2 d-print-none">
      <div class="card-body">
        <div class="row">

          <div class="col-md-2 col-6">
            <label>Apresentação</label>
            <select v-model="controle.apresentacao" class="form-select form-select-sm" id="apresentacao">
              <option v-for="item in apresentacoes" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <label>Status</label>
            <select v-model="controle.filtros.ativo" class="form-select form-select-sm" id="ativo">
              <option :value="null">Todos</option>
              <option :value="true">Ativos</option>
              <option :value="false">Inativos</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <label>Cidade</label>
            <DropdownCidadesAtiva v-model="controle.filtros.cidadeId" id="cidadeId" @selected="selecionouCidade" />
          </div>

          <div class="col-md-2 col-6">
            <label>Empresa</label>
            <DropdownEmpresas v-model="controle.filtros.empresaId" id="empresaId" @selected="selecionouEmpresa" />
          </div>

          <div class="col-md-2 col-6">
            <label>Com Dependentes</label>
            <select v-model="controle.filtros.comDependentes" class="form-select form-select-sm" id="comDependentes">
              <option :value="true">Sim</option>
              <option :value="false">Não</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <label>
              {{ controle.usarIdadeInicial ? 'Idade Inicial' : 'Dt Nasc Inicial' }}
              <a href="#" class="small" @click.prevent="alternarIdadeInicial" :title="controle.usarIdadeInicial ? 'Usar data' : 'Usar idade'">
                <i class="fa fa-repeat"></i>
              </a>
            </label>
            <input v-if="!controle.usarIdadeInicial" v-model="controle.filtros.dataNascimentoInicial" type="date" class="form-control form-control-sm" id="dataNascimentoInicial" />
            <input v-else v-model.number="controle.idadeInicial" @input="atualizarDataPelaIdadeInicial" type="number" min="0" class="form-control form-control-sm" id="idadeInicial" />
          </div>

          <div class="col-md-2 col-6">
            <label>
              {{ controle.usarIdadeFinal ? 'Idade Final' : 'Dt Nasc Final' }}
              <a href="#" class="small" @click.prevent="alternarIdadeFinal" :title="controle.usarIdadeFinal ? 'Usar data' : 'Usar idade'">
                <i class="fa fa-repeat"></i>
              </a>
            </label>
            <input v-if="!controle.usarIdadeFinal" v-model="controle.filtros.dataNascimentoFinal" type="date" class="form-control form-control-sm" id="dataNascimentoFinal" />
            <input v-else v-model.number="controle.idadeFinal" @input="atualizarDataPelaIdadeFinal" type="number" min="0" class="form-control form-control-sm" id="idadeFinal" />
          </div>

          <div class="col-md-2 col-6">
            <a class="btn btn-primary btn-sm mt-4" @click="pesquisar">
              <i class="fa fa-search me-1"></i> Pesquisar
            </a>
          </div>

          <div class="col-md-2 col-6">
            <div class="dropdown">
              <a class="btn btn-outline-secondary btn-sm dropdown-toggle mt-4" href="#" role="button"
                data-bs-toggle="dropdown" aria-expanded="false">
                <i class="fa fa-table-columns me-1"></i> Colunas
              </a>
              <ul class="dropdown-menu p-2" style="min-width: 200px;">
                <li v-for="col in colunasOpcionais" :key="col.chave">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" :id="'col_' + col.chave"
                      v-model="controle.colunasVisiveis[col.chave]">
                    <label class="form-check-label" :for="'col_' + col.chave">{{ col.label }}</label>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-md-2 col-6">
            <a class="btn btn-outline-secondary btn-sm mt-4" href="#" title="Gerar Excel (.xlsx)"
              @click.prevent="gerarExcel">
              <i class="fa fa-file-excel me-1"></i> Gerar Excel
            </a>
          </div>

        </div>
      </div>
    </div>

    <div class="col-md-12">

      <ReportView :titulo="'Rel Clientes'" :btnImprimir="true">

        <div v-if="controle.erro != ''" class="col-md-12 text-center">
          <div class="p-4 text-danger fw-14">{{ controle.erro }}</div>
        </div>

        <div v-if="controle.erro == '' && controle.pesquisando" class="col-md-12 text-center">
          <div class="p-4">
            <i class="fa fa-cog fa-2x fa-spin"></i>
            <div class="fw-16">Pesquisando...</div>
          </div>
        </div>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length > 0">

          <RelNormal v-if="controle.apresentacao === 'Normal'" :clientes="controle.itens" :filtros="resumoFiltros" />
          
          <RelFuneral v-if="controle.apresentacao === 'Assistência Funeral'" :clientes="controle.itens" :filtros="resumoFiltros" />

          <RelSeguro v-if="controle.apresentacao === 'Seguro'" :clientes="controle.itens" :filtros="resumoFiltros" />
          <!-- <ReportCabecalho :Titulo="'Relatório de Clientes'" :Filtros="resumoFiltros" /> -->

          <!-- Apresentação Normal -->
          <!-- <template v-if="controle.apresentacao === 'Normal'">

            <div class="mb-1 text-muted f12px">Total: {{ controle.itens.length }} cliente(s)</div>

            <div style="overflow-x: auto;">
            <table class="tabela table-sm table-bordered f12px" id="tabela-relatorio">
              <thead>
                <tr class="table-relatorio">
                  <th class="text-start col-ordenavel" @click="sortNormal.alternar('nome')">Nome <i :class="sortNormal.classeIcone('nome')"></i></th>
                  <th v-if="controle.colunasVisiveis.cpf" class="text-center  text-nowrap col-ordenavel" @click="sortNormal.alternar('cpf')">CPF <i :class="sortNormal.classeIcone('cpf')"></i></th>
                  <th v-if="controle.colunasVisiveis.nascimento" class="text-center  text-nowrap col-ordenavel" @click="sortNormal.alternar('nascimento')">Nasc. <i :class="sortNormal.classeIcone('nascimento')"></i></th>
                  <th v-if="controle.colunasVisiveis.idade" class="text-center  text-nowrap col-ordenavel" @click="sortNormal.alternar('idade')">Idade <i :class="sortNormal.classeIcone('idade')"></i></th>
                  <th v-if="controle.colunasVisiveis.telefone" class="text-center  text-nowrap col-ordenavel" @click="sortNormal.alternar('telefone')">Telefone <i :class="sortNormal.classeIcone('telefone')"></i></th>
                  <th v-if="controle.colunasVisiveis.cidade" class="text-start col-ordenavel" @click="sortNormal.alternar('cidade')">Cidade <i :class="sortNormal.classeIcone('cidade')"></i></th>
                  <th v-if="controle.colunasVisiveis.ativo" class="text-center  text-nowrap col-ordenavel" @click="sortNormal.alternar('ativo')">Ativo <i :class="sortNormal.classeIcone('ativo')"></i></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in itensOrdenados" :key="idx">
                  <td class="text-start  text-nowrap">{{ item.nome }}</td>
                  <td v-if="controle.colunasVisiveis.cpf" class="text-center  ">{{ item.cpf }}</td>
                  <td v-if="controle.colunasVisiveis.nascimento" class="text-center  text-nowrap">{{ fdata(item.dataNascimento) }}</td>
                  <td v-if="controle.colunasVisiveis.idade" class="text-center  text-nowrap">{{ calcIdade(item.dataNascimento) ?? '-' }}</td>
                  <td v-if="controle.colunasVisiveis.telefone" class="text-center  text-nowrap">{{ item.celular || item.telefone }}</td>
                  <td v-if="controle.colunasVisiveis.cidade" class="text-start  text-truncate">{{ item.cidadeNome }}</td>
                  <td v-if="controle.colunasVisiveis.ativo" class="text-center  text-nowrap">{{ SimNao(item.ativo) }}</td>
                </tr>
              </tbody>
            </table>
            </div>

          </template> -->

          <!-- Apresentação Assistência Funeral / Seguro -->
          <!-- <template v-else>

            <div class="mb-1 text-muted f12px">Total: {{ linhasPessoas.length }} pessoa(s)</div>

            <table class="tabela table-sm table-bordered f12px" id="tabela-relatorio-pessoas">
              <thead>
                <tr class="table-relatorio">
                  <th class="text-start text-nowrap col-ordenavel" @click="sortPessoas.alternar('nome')">Nome <i :class="sortPessoas.classeIcone('nome')"></i></th>
                  <th class="text-center  text-nowrap col-ordenavel" @click="sortPessoas.alternar('dataNascimento')">Data Nascimento <i :class="sortPessoas.classeIcone('dataNascimento')"></i></th>
                  <th class="text-center  text-nowrap col-ordenavel" @click="sortPessoas.alternar('cpf')">CPF <i :class="sortPessoas.classeIcone('cpf')"></i></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(linha, idx) in linhasPessoasOrdenadas" :key="idx" :class="linha.tipo == 'Titular' ? 'fw-bold' : ''">
                  <td class="text-start text-nowrap">{{ linha.nome }}</td>
                  <td class="text-center  text-nowrap">{{ fdata(linha.dataNascimento) }}</td>
                  <td class="text-center  text-nowrap">{{ linha.cpf }}</td>
                </tr>
              </tbody>
            </table>

          </template> -->

        </template>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length === 0 && controle.totalRegistros === 0 && controle.status === ''">
          <div class="text-center p-4 text-muted">
            Utilize os filtros acima e clique em <strong>Pesquisar</strong> para gerar o relatório.
          </div>
        </template>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length === 0 && controle.totalRegistros > 0">
          <div class="text-center p-4 text-muted">Nenhum cliente encontrado com os filtros selecionados.</div>
        </template>

      </ReportView>

    </div>
  </TrataTecla>
</template>

<style lang="scss" scoped>
.colCpf {
  width: 110px;
}
.colNasc {
  width: 85px;
}
.colIdade {
  width: 65px;
}
.colTelefone {
  width: 110px;
}
.colCidade {
  width: 140px;
}
.colAtivo {
  width: 55px;
}
#tabela-relatorio {
  table-layout: fixed;
  min-width: 1000px;
  width: 100%;
}
#tabela-relatorio-pessoas {
  table-layout: fixed;
  width: 100%;
}
</style>
